import { useState, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus, Pencil, Trash2, Upload } from "lucide-react";
import { queryClient } from "@/lib/queryClient";
import { formatCurrency } from "@/lib/currency";
import { Pagination } from "@/components/Pagination";

const getColorFromName = (name: string) => {
  const colors = ['bg-red-500', 'bg-blue-500', 'bg-green-500', 'bg-yellow-500', 'bg-purple-500', 'bg-pink-500'];
  const hash = name.split('').reduce((a, b) => a + b.charCodeAt(0), 0);
  return colors[hash % colors.length];
};

interface StarterEquipmentTabProps {
  starterEquipment: any[] | undefined;
  equipmentLoading: boolean;
  starterEquipmentCategories: any[];
  currency: string;
  isAdmin: boolean;
  equipmentPage: number;
  setEquipmentPage: (page: number) => void;
  itemsPerPage: number;
  setItemsPerPage: (size: number) => void;
  equipmentSearchQuery: string;
  setEquipmentSearchQuery: (query: string) => void;
  onAdd?: () => void;
  onEdit?: (item: any) => void;
  onDelete?: (id: string) => void;
  onUpload?: () => void;
  onView?: (item: any) => void;
}

export function StarterEquipmentTab({
  starterEquipment,
  equipmentLoading,
  starterEquipmentCategories,
  currency,
  isAdmin,
  equipmentPage,
  setEquipmentPage,
  itemsPerPage,
  setItemsPerPage,
  equipmentSearchQuery,
  setEquipmentSearchQuery,
  onAdd,
  onEdit,
  onDelete,
  onUpload,
  onView,
}: StarterEquipmentTabProps) {

  const filteredEquipment = useMemo(() => {
    if (!starterEquipment) return [];
    if (!equipmentSearchQuery) return starterEquipment;
    return starterEquipment.filter((item) =>
      item.name?.toLowerCase().includes(equipmentSearchQuery.toLowerCase())
    );
  }, [starterEquipment, equipmentSearchQuery]);

  const paginatedEquipment = useMemo(() => {
    const start = (equipmentPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    return filteredEquipment.slice(start, end);
  }, [filteredEquipment, equipmentPage, itemsPerPage]);

  const equipmentTotalPages = Math.max(1, Math.ceil((filteredEquipment?.length || 0) / itemsPerPage));

  return (
    <Card>
      <CardHeader className="flex flex-col gap-2 space-y-0">
          <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <CardTitle>Starter Equipment ({starterEquipment?.length || 0})</CardTitle>
            <CardDescription>Template equipment for new users</CardDescription>
          </div>
          <div className="flex gap-2">
            {isAdmin && onUpload && (
              <Button onClick={onUpload} variant="outline">
                <Upload className="h-4 w-4 mr-2" />
                Upload CSV
              </Button>
            )}
            {isAdmin && onAdd && (
              <Button onClick={onAdd}>
                <Plus className="h-4 w-4 mr-2" />
                Add Equipment
              </Button>
            )}
          </div>
          </div>
          <Input
              placeholder="Search equipment..."
              value={equipmentSearchQuery}
              onChange={(e) => setEquipmentSearchQuery(e.target.value)}
              className="w-full"
            />
        </CardHeader>
        <CardContent>
          {equipmentLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : starterEquipment && starterEquipment.length > 0 ? (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16">Image</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead className="text-right">Purchase Amount</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedEquipment.map((item: any) => (
                    <TableRow
                      key={item._id}
                      onClick={() => onView?.(item)}
                      className={onView ? "cursor-pointer hover:bg-muted/50" : ""}
                    >
                      <TableCell>
                        {item.image || item.imageUrl ? (
                          <img src={item.image || item.imageUrl} className="w-10 h-10 object-cover rounded" alt={item.name} />
                        ) : (
                          <div className={`w-10 h-10 rounded flex items-center justify-center text-white text-sm font-medium ${getColorFromName(item.name)}`}>
                            {item.name.charAt(0).toUpperCase()}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="font-medium">{item.name}</TableCell>
                      <TableCell>
                        {starterEquipmentCategories?.find((c: any) => c._id === item.categoryId)?.name || "-"}
                      </TableCell>
                      <TableCell className="text-right">
                        {item.purchaseAmount ? formatCurrency(item.purchaseAmount, currency) : "-"}
                      </TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-end gap-2">
                          {isAdmin && (
                            <>
                              {onEdit && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => onEdit(item)}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                              )}
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button variant="ghost" size="sm">
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete Equipment</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Are you sure you want to delete "{item.name}"? This action cannot be undone.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => onDelete?.(item._id || item.id)}
                                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                    >
                                      Delete
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
            <Pagination
              currentPage={equipmentPage}
              totalPages={equipmentTotalPages}
              totalItems={filteredEquipment?.length || 0}
              itemsPerPage={itemsPerPage}
              onPageChange={setEquipmentPage}
              onPageSizeChange={setItemsPerPage}
            />
            </>
          ) : (
            <p className="text-muted-foreground text-center py-8">
              No equipment added yet
            </p>
          )}
        </CardContent>
      </Card>
  );
}
